'use client'

import { HTMLAttributes, useContext, useRef, useState } from 'react'
import { IconTruckDelivery, IconX } from '@tabler/icons-react'
import clsx from 'clsx'

import useIsVisible from 'shared/hooks/useIsVisible'
import { NavigationOpenables } from 'shared/types/navigation'
import Button from 'shared/ui/Button'
import IconButton, { IconButtonProps } from 'shared/ui/IconButton/IconButton'
import Link, { LinkProps } from 'shared/ui/Link/Link'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'
import {
  OfflineContext,
  OfflineContextType
} from 'shared/ui/Offline/OfflineContext'
import { Popover } from 'shared/ui/Popover'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { PATHS } from 'shared/utils/paths'
import { addTestId } from 'shared/utils/tests'

import { useCheckDeliveryCoverage } from '../../hooks/useCheckDeliveryCoverage'
import { useOnboarding } from '../../providers/OnboardingContext'
import { DeliveryArea } from '../../submodules/area'
import DeliveryAreaForm from '../DeliveryAreaForm'

import styles from './GuestDeliveryAreaCheck.module.scss'

interface GuestDeliveryAreaCheckProps {
  savePostalCode?: boolean
  open: boolean
  setOpen: (name: NavigationOpenables, value: boolean) => void
}

const GuestDeliveryAreaCheck: React.FC<GuestDeliveryAreaCheckProps> = ({
  savePostalCode = false,
  open,
  setOpen
}) => {
  const {
    update,
    isDeliveryPossible,
    postalCodeAndCityContext,
    clearDeliveryPossible
  } = useCheckDeliveryCoverage(open)

  const { isOnboardingOpen, setIsOnboardingOpen } = useOnboarding()

  const anchorRef = useRef<HTMLButtonElement>(null)

  const [isLoading, setIsLoading] = useState(false)

  const isVisible = useIsVisible(anchorRef)

  const { isOnline, temporaryOfflineNotificationError } = useContext(
    OfflineContext
  ) as OfflineContextType

  const handleSubmit = async ({ postalCode, city }: DeliveryArea) => {
    if (!isOnline) {
      temporaryOfflineNotificationError()

      return
    }

    setIsLoading(true)

    try {
      const response = await update({
        postalCode: postalCode,
        city: city
      })

      if (response?.isSupported) {
        toast.success('Kod pocztowy został dodany. Życzymy udanych zakupów!')
        handleClose()
      }
    } catch (err) {
      console.error(getApiErrors(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpen = () => setOpen('deliveryReservationNoUser', true)
  const handleClose = () => setOpen('deliveryReservationNoUser', false)

  const handleClosePopover = () => setIsOnboardingOpen(false)

  return (
    <>
      <Button
        className={styles.trigger}
        size='large'
        {...addTestId('delivery-availability-check-button')}
        startIcon={<IconTruckDelivery />}
        onClick={handleOpen}
        aria-label='Sprawdź dostępność dostawy'
        variant='neutralOutline'
        ref={anchorRef}
      >
        <div className={styles.tabletAndWebContent}>Dodaj adres dostawy</div>
        <div className={styles.mobileContent}>Adres</div>
      </Button>
      <StyledPopover
        id='delivery-no-user-popover'
        aria-labelledby='delivery-no-user-popover-label'
        open={isOnboardingOpen && isVisible}
        anchorEl={anchorRef}
        onClose={handleClosePopover}
        backdrop={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <PopoverContent>
          <PopoverTop>
            <PopoverTitle id='delivery-no-user-popover-label'>
              Dodaj adres
            </PopoverTitle>
            <PopoverIcon
              onClick={handleClosePopover}
              aria-label='Zamknij'
              variant='transparent'
            >
              <IconX width={20} height={20} />
            </PopoverIcon>
          </PopoverTop>
          <PopoverDescription>
            <span> Dodaj swój adres lub </span>{' '}
            <PopoverLink href={PATHS.login}>zaloguj się</PopoverLink>, żeby
            upewnić się, że produkty w koszyku dostępne są w twojej lokalizacji.
          </PopoverDescription>
          <Button
            className={styles.popoverButton}
            variant='white'
            onClick={() => {
              handleClosePopover()
              handleOpen()
            }}
          >
            Podaj swój adres
          </Button>
        </PopoverContent>
      </StyledPopover>
      <Modal
        id='delivery-no-user-modal'
        open={open}
        onClose={handleClose}
        elementToReturnFocus={anchorRef}
        size='large'
      >
        <ModalTitle>Dostępność dostawy</ModalTitle>
        <ModalDescription visuallyHidden={true}>
          Dodaj swój adres, aby sprawdzić dostępność dostawy.
        </ModalDescription>

        <DeliveryAreaForm
          initValues={{
            postalCode: postalCodeAndCityContext.postalCode,
            city: postalCodeAndCityContext.city
          }}
          isLoading={isLoading}
          onSubmit={handleSubmit}
          buttonText={savePostalCode ? 'Ustaw' : 'Sprawdź'}
          onChange={clearDeliveryPossible}
          deliveryPossible={isDeliveryPossible}
        />
      </Modal>
    </>
  )
}

export default GuestDeliveryAreaCheck

const StyledPopover = Popover

const PopoverContent = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx(styles.popoverContent, className)} {...props} />
)

const PopoverTop = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx(styles.popoverTop, className)} {...props} />
)

const PopoverTitle = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx(styles.popoverTitle, className)} {...props} />
)

const PopoverDescription = ({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={clsx(styles.popoverDescription, className)} {...props} />
)

const PopoverLink = ({ className, ...props }: LinkProps) => (
  <Link className={clsx(styles.popoverLink, className)} {...props} />
)

const PopoverIcon = (props: IconButtonProps) => (
  <IconButton size='medium' {...props} />
)
